import { useCallback, useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useProfile } from './useProfile';
import { useToast } from '@/hooks/use-toast';
import { Analytics } from '@/lib/analytics';
import { getInclusiveLocalDayRange } from '@/lib/localDateRange';

export interface Food {
  id: string;
  name: string;
  brand?: string;
  barcode?: string;
  serving_size: number;
  serving_unit: string;
  calories_per_serving: number;
  protein_per_serving: number;
  carbs_per_serving: number;
  fat_per_serving: number;
  fiber_per_serving?: number;
  sugar_per_serving?: number;
  sodium_per_serving?: number;
  verified?: boolean;
}

export interface UserFood {
  id: string;
  user_id: string;
  name: string;
  serving_size: number;
  serving_unit: string;
  calories_per_serving: number;
  protein_per_serving: number;
  carbs_per_serving: number;
  fat_per_serving: number;
  fiber_per_serving?: number;
  sugar_per_serving?: number;
  sodium_per_serving?: number;
  verified?: boolean;
}

export interface NutritionGoals {
  id: string;
  user_id: string;
  daily_calories: number;
  daily_protein: number;
  daily_carbs: number;
  daily_fat: number;
  daily_fiber: number;
  daily_water_liters: number;
  goal_type: 'maintain' | 'lose_weight' | 'gain_weight' | 'gain_muscle';
  activity_level: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
  is_active: boolean;
  is_manual_override: boolean;
  created_at: string;
  updated_at: string;
}

export interface Meal {
  id: string;
  user_id: string;
  meal_date: string;
  meal_type: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  created_at: string;
}

export interface FoodEntry {
  id: string;
  meal_id: string;
  food_id?: string;
  user_food_id?: string;
  quantity: number;
  unit: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber?: number;
  sugar?: number;
  sodium?: number;
  food_name?: string;
  food?: Food;
  user_food?: UserFood;
}

export interface WaterIntake {
  id: string;
  user_id: string;
  intake_date: string;
  amount_liters: number;
  logged_at: string;
}

export interface DailyNutrition {
  date: string;
  meals: {
    breakfast: FoodEntry[];
    lunch: FoodEntry[];
    dinner: FoodEntry[];
    snack: FoodEntry[];
  };
  totals: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    sugar: number;
    sodium: number;
  };
  water_intake: number;
}

export const useComprehensiveNutrition = () => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isFetchingGoals, setIsFetchingGoals] = useState(false);
  const lastCalculationRef = useRef<number>(0);
  const CALCULATION_COOLDOWN = 3000; // 3 seconds between calculations
  
  const [foods, setFoods] = useState<Food[]>([]);
  const [userFoods, setUserFoods] = useState<UserFood[]>([]);
  const [nutritionGoals, setNutritionGoals] = useState<NutritionGoals | null>(null);
  const [dailyNutrition, setDailyNutrition] = useState<DailyNutrition | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Calculate BMR and TDEE based on user profile
  const calculateCalorieNeeds = () => {
    if (!profile || !profile.weight_kg || !profile.height_cm || !profile.birth_date) {
      return { bmr: 2000, tdee: 2400 }; // Default values
    }

    const age = new Date().getFullYear() - new Date(profile.birth_date).getFullYear();
    const weight = profile.weight_kg;
    const height = profile.height_cm;
    const gender = profile.gender || 'male';

    // Mifflin-St Jeor Formula (same as backend)
    let bmr: number;
    if (gender.toLowerCase() === 'female') {
      bmr = (10 * weight) + (6.25 * height) - (5 * age) - 161;
    } else {
      bmr = (10 * weight) + (6.25 * height) - (5 * age) + 5;
    }

    // Use exact same activity multipliers as backend
    const activityMultipliers = {
      sedentary: 1.20,      // Little to no exercise
      light: 1.375,         // Exercise 1-3 days/week
      moderate: 1.55,       // Exercise 3-5 days/week
      very_active: 1.725,   // Exercise 6-7 days/week (maps from 'active')
      extra_active: 1.90    // Physical job + daily intense exercise
    };

    let activityLevel = profile.activity_level || 'moderate';
    // Map 'active' to 'very_active' for consistency with backend
    if (activityLevel === 'active') {
      activityLevel = 'very_active';
    }
    const tdee = bmr * activityMultipliers[activityLevel as keyof typeof activityMultipliers];

    return { bmr: Math.round(bmr), tdee: Math.round(tdee) };
  };

  // Search foods from database and external API
  const searchFoods = useCallback(async (query: string): Promise<Food[]> => {
    if (!query.trim()) return [];

    try {
      // Remove spaces from query for better matching
      const searchTerms = query.trim().split(/\s+/).join('%');
      
      // Search local database first - search both with and without spaces
      const { data, error } = await supabase
        .from('foods')
        .select('*')
        .or(`name.ilike.%${query}%,brand.ilike.%${query}%,name.ilike.%${searchTerms}%`)
        .limit(20);

      if (error) throw error;
      
      console.log('Local search results:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('Error searching foods:', error);
      return [];
    }
  }, []);

  // Fetch user's custom foods
  const fetchUserFoods = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_foods')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      setUserFoods(data || []);
    } catch (error) {
      console.error('Error fetching user foods:', error);
    }
  }, [user?.id]);

  // Create custom food
  const createCustomFood = async (foodData: Omit<UserFood, 'id' | 'user_id'>) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_foods')
        .insert([{ ...foodData, user_id: user.id }])
        .select()
        .single();

      if (error) throw error;
      await fetchUserFoods();
      return data;
    } catch (error) {
      console.error('Error creating custom food:', error);
      throw error;
    }
  };

  // Calculate AI-based nutrition goals
  const calculateAINutritionGoals = async () => {
    if (!user || isCalculating) {
      console.log('Skipping calculation - already in progress or no user');
      return;
    }

    // Client-side rate limiting
    const now = Date.now();
    const timeSinceLastCall = now - lastCalculationRef.current;
    if (timeSinceLastCall < CALCULATION_COOLDOWN) {
      const waitTime = Math.ceil((CALCULATION_COOLDOWN - timeSinceLastCall) / 1000);
      toast({
        title: "Please wait",
        description: `Please wait ${waitTime} more second${waitTime > 1 ? 's' : ''} before recalculating.`,
        variant: "default",
      });
      throw new Error('Rate limited - please wait');
    }

    try {
      setIsCalculating(true);
      setLoading(true);
      lastCalculationRef.current = now;
      
      // Get fresh session token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('No valid session found. Please sign in again.');
      }

      const { data, error } = await supabase.functions.invoke('calculate-nutrition-goals', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        // Handle rate limit errors gracefully
        if (error.message?.includes('too_many_requests') || error.message?.includes('429')) {
          toast({
            title: "Please slow down",
            description: "You're calculating too quickly. Please wait a few seconds and try again.",
            variant: "destructive",
          });
        }
        throw error;
      }

      console.log('AI nutrition goals calculated:', data);
      
      // Refresh goals after calculation
      await fetchNutritionGoals();
      
      return data;
    } catch (error) {
      console.error('Error calculating AI nutrition goals:', error);
      throw error;
    } finally {
      setLoading(false);
      setIsCalculating(false);
    }
  };

  // Recalculate goals ONLY when profile changes significantly
  // Never auto-recalculate based on time alone to prevent goal drift
  // IMPORTANT: Never recalculate if user has manually set their goals
  const recalculateGoalsIfNeeded = async (force = false) => {
    if (!profile || !user || isCalculating) {
      console.log('Skipping recalculation - conditions not met or already calculating');
      return;
    }
    
    // CRITICAL: Respect user's manual overrides - never auto-recalculate manually set goals
    if (nutritionGoals?.is_manual_override && !force) {
      console.log('Skipping recalculation - user has manually set their goals (is_manual_override=true)');
      return;
    }
    
    if (force) {
      console.log('Force recalculating nutrition goals...');
      // When force recalculating, clear the manual override flag
      if (nutritionGoals?.is_manual_override) {
        await supabase
          .from('user_nutrition_goals')
          .update({ is_manual_override: false })
          .eq('id', nutritionGoals.id);
      }
      await calculateAINutritionGoals();
      return;
    }
    
    if (!nutritionGoals) {
      // No goals exist, create them once
      await fetchNutritionGoals();
      return;
    }
    
    // IMPORTANT: Don't auto-recalculate. Goals should remain stable unless:
    // 1. User explicitly requests recalculation (force=true)
    // 2. User updates their profile with significant changes AND hasn't manually set goals
    // This prevents AI calculation variance from changing goals unexpectedly
    console.log('Goals exist and no force recalculation requested - keeping current goals stable');
  };

  // Fetch or create nutrition goals
  const fetchNutritionGoals = async () => {
    if (!user || isFetchingGoals) {
      console.log('Skipping fetchNutritionGoals - already in progress or no user');
      return;
    }

    try {
      setIsFetchingGoals(true);
      // Get the most recent active goal, and clean up duplicates
      const { data: allGoals, error: fetchError } = await supabase
        .from('user_nutrition_goals')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      if (allGoals && allGoals.length > 0) {
        const latestGoal = allGoals[0];
        setNutritionGoals(latestGoal as NutritionGoals);
        
        // Clean up duplicate goals if any exist
        if (allGoals.length > 1) {
          const duplicateIds = allGoals.slice(1).map(goal => goal.id);
          await supabase
            .from('user_nutrition_goals')
            .update({ is_active: false })
            .in('id', duplicateIds);
        }
        return;
      }

      if (!allGoals || allGoals.length === 0) {
        // Use AI to calculate personalized goals if profile is complete
        if (profile?.weight_kg && profile?.height_cm) {
          console.log('No nutrition goals found, calculating with AI...');
          await calculateAINutritionGoals();
          return; // fetchNutritionGoals will be called again after AI calculation
        } else {
          // Fallback to manual calculation if profile is incomplete
          const { tdee } = calculateCalorieNeeds();
          
          // Determine goal type from specific goals
          let goalType = 'maintain';
          if (profile?.specific_goals) {
            const specificGoals = profile.specific_goals.toLowerCase();
            if (specificGoals.includes('lose') || specificGoals.includes('loose') || specificGoals.includes('weight loss') || specificGoals.includes('go to')) {
              goalType = 'lose_weight';
            } else if (specificGoals.includes('gain') || specificGoals.includes('build') || specificGoals.includes('muscle')) {
              goalType = 'gain_muscle';
            }
          }
          
          // Adjust calories based on goal type
          let targetCalories = tdee;
          if (goalType === 'lose_weight') {
            targetCalories = Math.round(tdee - 400); // 400 calorie deficit for ~0.8lb/week loss
          } else if (goalType === 'gain_muscle') {
            targetCalories = Math.round(tdee + 300); // 300 calorie surplus for muscle gain
          }
          
          const defaultGoals = {
            user_id: user.id,
            daily_calories: targetCalories,
            daily_protein: Math.round(profile?.weight_kg ? profile.weight_kg * 2 : 140), // 2g per kg bodyweight
            daily_carbs: Math.round(targetCalories * 0.4 / 4), // 40% from carbs
            daily_fat: Math.round(targetCalories * 0.25 / 9), // 25% from fat
            daily_fiber: Math.round(targetCalories / 1000 * 14), // 14g per 1000 calories
            daily_water_liters: Math.round((profile?.weight_kg || 70) * 0.035 * 10) / 10, // 35ml per kg bodyweight
            goal_type: goalType,
            activity_level: profile?.activity_level || 'moderate',
            is_active: true,
            is_manual_override: false // Calculated goals, not manually set
          };

          const { data: newGoals, error: createError } = await supabase
            .from('user_nutrition_goals')
            .insert([defaultGoals])
            .select()
            .single();

          if (createError) throw createError;
          setNutritionGoals(newGoals as NutritionGoals);
        }
      }
    } catch (error) {
      console.error('Error fetching nutrition goals:', error);
      
      // Fallback to manual calculation if everything fails
      const { tdee } = calculateCalorieNeeds();
      
      // Determine goal type from specific goals
      let goalType = 'maintain';
      if (profile?.specific_goals) {
        const specificGoals = profile.specific_goals.toLowerCase();
        if (specificGoals.includes('lose') || specificGoals.includes('loose') || specificGoals.includes('weight loss') || specificGoals.includes('go to')) {
          goalType = 'lose_weight';
        } else if (specificGoals.includes('gain') || specificGoals.includes('build') || specificGoals.includes('muscle')) {
          goalType = 'gain_muscle';
        }
      }
      
      // Adjust calories based on goal type
      let targetCalories = tdee;
      if (goalType === 'lose_weight') {
        targetCalories = Math.round(tdee - 400); // 400 calorie deficit
      } else if (goalType === 'gain_muscle') {
        targetCalories = Math.round(tdee + 300); // 300 calorie surplus
      }
      
      const fallbackGoals = {
        user_id: user.id,
        daily_calories: targetCalories,
        daily_protein: Math.round(profile?.weight_kg ? profile.weight_kg * 2 : 140),
        daily_carbs: Math.round(targetCalories * 0.4 / 4),
        daily_fat: Math.round(targetCalories * 0.25 / 9),
        daily_fiber: Math.round(targetCalories / 1000 * 14),
        daily_water_liters: Math.round((profile?.weight_kg || 70) * 0.035 * 10) / 10,
        goal_type: goalType,
        activity_level: profile?.activity_level || 'moderate',
        is_active: true,
        is_manual_override: false // Calculated goals, not manually set
      };

      try {
        const { data: createdGoals, error: createError } = await supabase
          .from('user_nutrition_goals')
          .insert([fallbackGoals])
          .select()
          .single();

        if (!createError) {
          setNutritionGoals(createdGoals as NutritionGoals);
        }
      } catch (fallbackError) {
        console.error('Error creating fallback goals:', fallbackError);
      }
    } finally {
      setIsFetchingGoals(false);
    }
  };

  // Update nutrition goals
  // When user manually updates calories, protein, carbs, or fat, mark as manual override
  // This prevents auto-recalculation from overwriting user-set values
  const updateNutritionGoals = async (updates: Partial<NutritionGoals>) => {
    if (!user || !nutritionGoals) return;

    try {
      // Check if this is a manual update to key nutrition values
      const isManualNutritionUpdate = 
        updates.daily_calories !== undefined || 
        updates.daily_protein !== undefined || 
        updates.daily_carbs !== undefined || 
        updates.daily_fat !== undefined;
      
      // If user is manually setting nutrition values, mark as manual override
      const finalUpdates = isManualNutritionUpdate 
        ? { ...updates, is_manual_override: true }
        : updates;
      
      const { data, error } = await supabase
        .from('user_nutrition_goals')
        .update(finalUpdates)
        .eq('id', nutritionGoals.id)
        .select()
        .single();

      if (error) throw error;
      setNutritionGoals(data as NutritionGoals);
      
      // Track nutrition goal progress
      if (isManualNutritionUpdate) {
        console.log('Manual nutrition goal update - goals will persist until changed');
        Analytics.nutritionGoalProgress({
          calories: data.daily_calories,
          protein: data.daily_protein,
          carbs: data.daily_carbs,
          fat: data.daily_fat
        });
      }
      return data;
    } catch (error) {
      console.error('Error updating nutrition goals:', error);
      throw error;
    }
  };

  // Fetch daily nutrition data
  const fetchDailyNutrition = async (date: string) => {
    if (!user) return;

    try {
      // Fetch meals for the date
      const { data: meals, error: mealsError } = await supabase
        .from('meals')
        .select('*')
        .eq('user_id', user.id)
        .eq('meal_date', date);

      if (mealsError) throw mealsError;

      // Fetch food entries and food details in batches. This avoids one
      // Supabase round-trip per logged item on busy nutrition days.
      const allFoodEntries: any[] = [];
      if (meals && meals.length > 0) {
        const mealIds = meals.map(meal => meal.id);
        
        // Fetch food entries
        const { data: foodEntries, error: entriesError } = await supabase
          .from('food_entries')
          .select('*')
          .in('meal_id', mealIds);

        if (entriesError) throw entriesError;

        if (foodEntries && foodEntries.length > 0) {
          const foodIds = [...new Set(foodEntries.map((entry: any) => entry.food_id).filter(Boolean))];
          const userFoodIds = [...new Set(foodEntries.map((entry: any) => entry.user_food_id).filter(Boolean))];

          const [foodsResult, basicFoodsResult, userFoodsResult] = await Promise.all([
            foodIds.length > 0
              ? supabase.from('foods').select('*').in('id', foodIds)
              : Promise.resolve({ data: [], error: null }),
            foodIds.length > 0
              ? supabase.from('basic_foods').select('*').in('id', foodIds)
              : Promise.resolve({ data: [], error: null }),
            userFoodIds.length > 0
              ? supabase.from('user_foods').select('*').in('id', userFoodIds)
              : Promise.resolve({ data: [], error: null }),
          ]);

          if (foodsResult.error) throw foodsResult.error;
          if (basicFoodsResult.error) throw basicFoodsResult.error;
          if (userFoodsResult.error) throw userFoodsResult.error;

          const foodsById = new Map((foodsResult.data || []).map((food: any) => [food.id, food]));
          const basicFoodsById = new Map(
            (basicFoodsResult.data || []).map((basicFood: any) => [
              basicFood.id,
              {
                ...basicFood,
                brand: basicFood.category,
                verified: true,
                is_public: true,
                created_by: null,
                barcode: null,
              },
            ])
          );
          const userFoodsById = new Map((userFoodsResult.data || []).map((food: any) => [food.id, food]));

          foodEntries.forEach((entry: any) => {
            const foodDetail: any = { ...entry };
            if (entry.food_id) {
              const food = foodsById.get(entry.food_id) ?? basicFoodsById.get(entry.food_id);
              if (food) foodDetail.food = food;
            } else if (entry.user_food_id) {
              const userFood = userFoodsById.get(entry.user_food_id);
              if (userFood) foodDetail.user_food = userFood;
            }
            allFoodEntries.push(foodDetail);
          });
        }
      }

      // Fetch water intake
      const { data: waterData, error: waterError } = await supabase
        .from('water_intake')
        .select('*')
        .eq('user_id', user.id)
        .eq('intake_date', date);

      if (waterError) throw waterError;

      // Organize meals by type
      const mealsByType = {
        breakfast: [] as FoodEntry[],
        lunch: [] as FoodEntry[],
        dinner: [] as FoodEntry[],
        snack: [] as FoodEntry[]
      };

      let totalCalories = 0;
      let totalProtein = 0;
      let totalCarbs = 0;
      let totalFat = 0;
      let totalFiber = 0;
      let totalSugar = 0;
      let totalSodium = 0;

      // Map food entries to their respective meals
      meals?.forEach((meal: any) => {
        const mealEntries = allFoodEntries.filter(entry => entry.meal_id === meal.id);
        
        mealEntries.forEach((entry: any) => {
          mealsByType[meal.meal_type as keyof typeof mealsByType].push(entry);

          totalCalories += entry.calories || 0;
          totalProtein += entry.protein || 0;
          totalCarbs += entry.carbs || 0;
          totalFat += entry.fat || 0;
          totalFiber += entry.fiber || 0;
          totalSugar += entry.sugar || 0;
          totalSodium += entry.sodium || 0;
        });
      });

      const totalWater = waterData?.reduce((sum, intake) => sum + (intake.amount_liters || 0), 0) || 0;

      const dailyData: DailyNutrition = {
        date,
        meals: mealsByType,
        totals: {
          calories: totalCalories,
          protein: totalProtein,
          carbs: totalCarbs,
          fat: totalFat,
          fiber: totalFiber,
          sugar: totalSugar,
          sodium: totalSodium
        },
        water_intake: totalWater
      };

      setDailyNutrition(dailyData);
      return dailyData;
    } catch (error) {
      console.error('Error fetching daily nutrition:', error);
    }
  };

  // Log food to meal
  const logFoodToMeal = async (
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack',
    food: Food | UserFood,
    quantity: number,
    unit: string,
    date: string = selectedDate
  ) => {
    if (!user) {
      console.error('logFoodToMeal: No user found');
      throw new Error('User not authenticated');
    }

    console.log('logFoodToMeal: Starting with params:', { 
      mealType, 
      foodName: food.name, 
      quantity, 
      unit, 
      date, 
      userId: user.id 
    });

    try {
      // Find or create meal — use limit(1), not maybeSingle: duplicate rows cause PGRST116.
      const { data: existingMeals, error: mealError } = await supabase
        .from('meals')
        .select('*')
        .eq('user_id', user.id)
        .eq('meal_date', date)
        .eq('meal_type', mealType)
        .order('created_at', { ascending: true })
        .limit(1);

      console.log('logFoodToMeal: Meal query result:', {
        count: existingMeals?.length ?? 0,
        mealError,
      });

      if (mealError) {
        console.error('logFoodToMeal: Error fetching meal:', mealError);
        throw mealError;
      }

      let meal = existingMeals?.[0] ?? null;

      if (!meal) {
        console.log('logFoodToMeal: Creating new meal');
        const { data: newMeal, error: createMealError } = await supabase
          .from('meals')
          .insert([{
            user_id: user.id,
            meal_date: date,
            meal_type: mealType
          }])
          .select()
          .single();

        console.log('logFoodToMeal: New meal creation result:', { newMeal, createMealError });

        if (createMealError?.code === '23505') {
          const { data: retryMeals, error: retryError } = await supabase
            .from('meals')
            .select('*')
            .eq('user_id', user.id)
            .eq('meal_date', date)
            .eq('meal_type', mealType)
            .order('created_at', { ascending: true })
            .limit(1);
          if (retryError) throw retryError;
          meal = retryMeals?.[0] ?? null;
        } else if (createMealError) {
          console.error('logFoodToMeal: Error creating meal:', createMealError);
          throw createMealError;
        } else {
          meal = newMeal;
        }
      }

      if (!meal) {
        throw new Error('Could not find or create meal');
      }

      // Calculate nutrition values based on quantity
      const multiplier = unit === 'serving' ? quantity : (quantity / food.serving_size);
      
      // Check if the ID is a valid UUID (to exclude external API IDs like "off_123" or "usda_456")
      const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(food.id);
      
      let foodId = null;
      let userFoodId = null;
      
      // If it's a valid UUID, use it directly
      if (isValidUUID) {
        if ('user_id' in food) {
          userFoodId = food.id;
        } else {
          foodId = food.id;
        }
      } else {
        // For external API foods (USDA, Open Food Facts), save to foods table first
        const { data: existingFood } = await supabase
          .from('foods')
          .select('id')
          .eq('name', food.name)
          .eq('brand', (food as any).brand || 'Unknown')
          .maybeSingle();
        
        if (existingFood) {
          foodId = existingFood.id;
        } else {
          // Create new food entry
          const { data: newFood, error: createFoodError } = await supabase
            .from('foods')
            .insert([{
              name: food.name,
              brand: (food as any).brand || 'Unknown',
              barcode: (food as any).barcode || null,
              serving_size: food.serving_size,
              serving_unit: food.serving_unit,
              calories_per_serving: food.calories_per_serving,
              protein_per_serving: food.protein_per_serving,
              carbs_per_serving: food.carbs_per_serving,
              fat_per_serving: food.fat_per_serving,
              fiber_per_serving: food.fiber_per_serving || 0,
              sugar_per_serving: food.sugar_per_serving || 0,
              sodium_per_serving: food.sodium_per_serving || 0,
              verified: true,
              is_public: true,
              created_by: user.id
            }])
            .select()
            .single();
          
          if (createFoodError) {
            console.error('Error creating food:', createFoodError);
            // If food creation fails, we can still proceed with food_name only
            // The food entry will be saved without a food_id reference
            foodId = null;
          } else if (newFood) {
            foodId = newFood.id;
          }
        }
      }
      
      const foodEntry = {
        meal_id: meal.id,
        food_name: food.name,
        food_id: foodId,
        user_food_id: userFoodId,
        quantity,
        unit,
        calories: Math.round(food.calories_per_serving * multiplier),
        protein: Math.round(food.protein_per_serving * multiplier),
        carbs: Math.round(food.carbs_per_serving * multiplier),
        fat: Math.round(food.fat_per_serving * multiplier),
        fiber: Math.round((food.fiber_per_serving || 0) * multiplier),
        sugar: Math.round((food.sugar_per_serving || 0) * multiplier),
        sodium: Math.round((food.sodium_per_serving || 0) * multiplier)
      };

      console.log('logFoodToMeal: Inserting food entry:', foodEntry);

      const { data, error } = await supabase
        .from('food_entries')
        .insert([foodEntry])
        .select()
        .single();

      console.log('logFoodToMeal: Food entry insert result:', { data, error });

      if (error) {
        console.error('logFoodToMeal: Error inserting food entry:', error);
        throw error;
      }

      // Refresh daily nutrition in background (non-blocking) to prevent errors from blocking success
      // Only throw if the primary insert failed, not if refresh fails
      console.log('logFoodToMeal: Refreshing daily nutrition');
      fetchDailyNutrition(date).catch(err => {
        console.warn('logFoodToMeal: Background refresh failed (non-critical):', err);
        // Don't throw - the food was successfully inserted
      });
      
      console.log('logFoodToMeal: Successfully completed');
      return data;
    } catch (error) {
      console.error('Error logging food to meal:', error);
      throw error;
    }
  };

  // Log water intake
  const logWaterIntake = async (amount: number, date: string = selectedDate) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('water_intake')
        .insert([{
          user_id: user.id,
          intake_date: date,
          amount_liters: amount
        }])
        .select()
        .single();

      if (error) throw error;

      // Refresh daily nutrition
      await fetchDailyNutrition(date);
      return data;
    } catch (error) {
      console.error('Error logging water intake:', error);
      throw error;
    }
  };

  // Delete food entry
  const deleteFoodEntry = async (entryId: string) => {
    try {
      const { error } = await supabase
        .from('food_entries')
        .delete()
        .eq('id', entryId);

      if (error) throw error;

      // Refresh daily nutrition
      await fetchDailyNutrition(selectedDate);
    } catch (error) {
      console.error('Error deleting food entry:', error);
      throw error;
    }
  };

  // Update food entry
  const updateFoodEntry = async (entryId: string, updates: {
    quantity?: number;
    calories?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
    fiber?: number;
    sugar?: number;
    sodium?: number;
  }) => {
    try {
      const { error } = await supabase
        .from('food_entries')
        .update(updates)
        .eq('id', entryId);

      if (error) throw error;

      // Refresh daily nutrition
      await fetchDailyNutrition(selectedDate);
    } catch (error) {
      console.error('Error updating food entry:', error);
      throw error;
    }
  };

  // Get nutrition progress for date range
  const getNutritionProgress = async (days: number = 7) => {
    if (!user) return [];

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const results: DailyNutrition[] = [];
      const current = new Date(startDate);
      
      // Fetch sequentially to avoid network saturation / rate limits
      while (current <= endDate) {
        const dateStr = current.toISOString().split('T')[0];
        const day = await fetchDailyNutrition(dateStr);
        if (day) results.push(day);
        current.setDate(current.getDate() + 1);
      }

      return results;
    } catch (error) {
      console.error('Error getting nutrition progress:', error);
      return [];
    }
  };
  
  // Lightweight daily totals range to reduce requests
  const getNutritionTotalsRange = async (days: number = 30) => {
    if (!user) return [];
    try {
      const { startDate, endDate, startStr, endStr } = getInclusiveLocalDayRange(days);

      // Fetch meals in range (paginate past PostgREST 1000-row default)
      const meals: { id: string; meal_date: string }[] = [];
      {
        const pageSize = 1000;
        let from = 0;
        for (;;) {
          const { data: page, error: mealsError } = await supabase
            .from('meals')
            .select('id, meal_date')
            .eq('user_id', user.id)
            .gte('meal_date', startStr)
            .lte('meal_date', endStr)
            .range(from, from + pageSize - 1);
          if (mealsError) throw mealsError;
          const batch = page || [];
          meals.push(...batch);
          if (batch.length < pageSize) break;
          from += pageSize;
        }
      }

      const mealIds = meals.map((m) => m.id);
      const dateByMealId = new Map<string, string>(meals.map((m) => [m.id, m.meal_date]));

      let entries: any[] = [];
      if (mealIds.length > 0) {
        // Chunk `.in()` — PostgREST URL / row limits drop food when many meals exist
        const CHUNK = 100;
        for (let i = 0; i < mealIds.length; i += CHUNK) {
          const chunk = mealIds.slice(i, i + CHUNK);
          const { data: foodEntries, error: entriesError } = await supabase
            .from('food_entries')
            .select('meal_id, calories, protein')
            .in('meal_id', chunk)
            .limit(5000);
          if (entriesError) throw entriesError;
          if (foodEntries?.length) entries = entries.concat(foodEntries);
        }
      }

      // Build zeroed map for each day in range
      const resultsMap = new Map<string, { date: string; totals: { calories: number; protein: number; carbs: number; fat: number; fiber: number; sugar: number; sodium: number } }>();
      const cursor = new Date(startDate);
      while (cursor <= endDate) {
        const d = format(cursor, 'yyyy-MM-dd');
        resultsMap.set(d, {
          date: d,
          totals: { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, sodium: 0 }
        });
        cursor.setDate(cursor.getDate() + 1);
      }

      // Aggregate entries by their meal's date
      for (const e of entries) {
        const d = dateByMealId.get(e.meal_id);
        if (!d) continue;
        const rec = resultsMap.get(d);
        if (!rec) continue;
        rec.totals.calories += e.calories || 0;
        rec.totals.protein += e.protein || 0;
      }

      // Return as ordered array
      return Array.from(resultsMap.values()).sort((a, b) => a.date.localeCompare(b.date));
    } catch (error) {
      console.error('Error getting nutrition totals range:', error);
      return [];
    }
  };
  
  // Fetch user-level data only when user changes - goals are FIXED and must NOT refetch on date change
  useEffect(() => {
    if (user) {
      fetchUserFoods();
      fetchNutritionGoals();
    }
  }, [user]);

  // Fetch date-specific nutrition data when user or selected date changes
  useEffect(() => {
    if (user) {
      fetchDailyNutrition(selectedDate);
    }
  }, [user, selectedDate]);

  // Only recalculate when user explicitly changes critical profile fields
  // This prevents unnecessary recalculations that cause goal drift
  // CRITICAL: Never auto-recalculate if user has manually set their goals
  useEffect(() => {
    if (!user || !profile || !nutritionGoals) return;
    
    // CRITICAL: Respect user's manual overrides - never auto-recalculate manually set goals
    if (nutritionGoals.is_manual_override) {
      console.log('Skipping auto-recalculation - user has manually set their goals');
      return;
    }
    
    // Check if weight changed by more than 2kg or activity level changed
    const { tdee } = calculateCalorieNeeds();
    const currentCalorieTarget = nutritionGoals.daily_calories;
    const calorieDifference = Math.abs(tdee - currentCalorieTarget);
    
    const significantProfileChange = 
      calorieDifference > 300 || // TDEE changed by more than 300 calories
      profile.activity_level !== nutritionGoals.activity_level;
    
    if (significantProfileChange) {
      console.log('Significant profile change detected, recalculating goals...');
      console.log('TDEE:', tdee, 'Current target:', currentCalorieTarget, 'Difference:', calorieDifference);
      calculateAINutritionGoals();
    }
  }, [profile?.weight_kg, profile?.activity_level]);

  // Get recent foods for quick logging
  const getRecentFoods = async (limit: number = 20) => {
    if (!user) return [];

    try {
      // Get most recent food entries
      const { data: recentEntries, error } = await supabase
        .from('food_entries')
        .select(`
          food_id,
          user_food_id,
          created_at,
          quantity,
          unit,
          food_name,
          meal_id
        `)
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(limit * 2); // Get more entries to account for duplicates

      if (error) {
        console.error('Recent foods query error:', error);
        throw error;
      }

      console.log('Recent entries found:', recentEntries?.length || 0);

      // Verify meals belong to current user
      const recentFoods = new Map();
      
      for (const entry of recentEntries || []) {
        // Check if meal belongs to user
        const { data: meal } = await supabase
          .from('meals')
          .select('user_id')
          .eq('id', entry.meal_id)
          .eq('user_id', user.id)
          .maybeSingle();
          
        if (!meal) continue; // Skip if meal doesn't belong to user
        
        let foodKey = '';
        let foodData: any = null;
        
        if (entry.food_id && !recentFoods.has(`food_${entry.food_id}`)) {
          const { data: food, error: foodError } = await supabase
            .from('foods')
            .select('*')
            .eq('id', entry.food_id)
            .maybeSingle();
          if (food && !foodError) {
            foodKey = `food_${entry.food_id}`;
            foodData = { 
              ...food, 
              lastUsed: entry.created_at, 
              lastQuantity: entry.quantity, 
              lastUnit: entry.unit 
            };
          }
        } else if (entry.user_food_id && !recentFoods.has(`user_food_${entry.user_food_id}`)) {
          const { data: userFood, error: userFoodError } = await supabase
            .from('user_foods')
            .select('*')
            .eq('id', entry.user_food_id)
            .maybeSingle();
          if (userFood && !userFoodError) {
            foodKey = `user_food_${entry.user_food_id}`;
            foodData = { 
              ...userFood, 
              lastUsed: entry.created_at, 
              lastQuantity: entry.quantity, 
              lastUnit: entry.unit 
            };
          }
        } else if (entry.food_name && !entry.food_id && !entry.user_food_id) {
          // Handle foods logged with just food_name (from external APIs)
          const nameKey = `name_${entry.food_name}`;
          if (!recentFoods.has(nameKey)) {
            foodKey = nameKey;
            foodData = {
              id: nameKey,
              name: entry.food_name,
              lastUsed: entry.created_at,
              lastQuantity: entry.quantity,
              lastUnit: entry.unit,
              // We don't have full nutrition data for name-only entries
              calories_per_serving: 0,
              protein_per_serving: 0,
              carbs_per_serving: 0,
              fat_per_serving: 0,
              serving_size: 100,
              serving_unit: 'g'
            };
          }
        }
        
        if (foodKey && foodData && !recentFoods.has(foodKey)) {
          recentFoods.set(foodKey, foodData);
          if (recentFoods.size >= limit) break;
        }
      }

      console.log('Recent foods processed:', recentFoods.size);
      return Array.from(recentFoods.values());
    } catch (error) {
      console.error('Error fetching recent foods:', error);
      return [];
    }
  };

  return {
    loading,
    isCalculating,
    foods,
    userFoods,
    nutritionGoals,
    dailyNutrition,
    selectedDate,
    setSelectedDate,
    searchFoods,
    createCustomFood,
    updateNutritionGoals,
    logFoodToMeal,
    logWaterIntake,
    deleteFoodEntry,
    updateFoodEntry,
    fetchDailyNutrition,
    getNutritionProgress,
    getNutritionTotalsRange,
    calculateCalorieNeeds,
    calculateAINutritionGoals,
    fetchUserFoods,
    fetchNutritionGoals,
    recalculateGoalsIfNeeded,
    getRecentFoods
  };
};